
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Clock, ArrowRight } from 'lucide-react';

interface AuthSuccessViewProps {
  onRedirect?: () => void;
}

const AuthSuccessView = ({ onRedirect }: AuthSuccessViewProps) => {
  const navigate = useNavigate();
  const [redirectProgress, setRedirectProgress] = useState(0);
  const redirectTimerRef = useRef<number | null>(null);
  
  // Redirect timer duration in milliseconds
  const REDIRECT_DURATION = 500;
  const PROGRESS_INTERVAL = 50; // Update progress every 50ms

  // Handle redirect to home
  const handleRedirectToHome = () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }
    
    if (onRedirect) {
      onRedirect();
    }
    
    navigate('/');
  };

  // Handle automatic redirect with progress
  useEffect(() => {
    const startTime = Date.now();
    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / REDIRECT_DURATION) * 100, 100);
      setRedirectProgress(progress);
      
      if (elapsed < REDIRECT_DURATION) {
        redirectTimerRef.current = window.setTimeout(updateProgress, PROGRESS_INTERVAL);
      } else {
        handleRedirectToHome();
      }
    };
    
    redirectTimerRef.current = window.setTimeout(updateProgress, PROGRESS_INTERVAL);
    
    // Cleanup function
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="text-center py-4">
      <div className="mb-4 text-green-600">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-lg font-medium">Authentication Successful!</h3>
      <p className="text-sm text-muted-foreground mt-2 mb-4">
        Redirecting to the homepage...
      </p>
      
      <div className="w-full max-w-xs mx-auto space-y-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <Progress value={redirectProgress} className="h-2" />
        </div>
        
        <Button 
          onClick={handleRedirectToHome}
          variant="outline"
          className="w-full"
          size="sm"
        >
          Continue Now <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default AuthSuccessView;
